/**
 * useCopyPaste.ts - Copy/paste functionality for automation flow canvas
 *
 * Provides clipboard operations for nodes:
 * - Copy selected nodes and their connecting edges
 * - Paste with position offset and new IDs
 * - Exclude trigger nodes (can't duplicate triggers)
 *
 * Related:
 *   - components/AutomationFlowCanvas.tsx - Keyboard shortcuts
 *   - utils/nodeFactory.ts - ID generation
 */

import { useCallback, useRef, useState } from 'react';
import * as Api from '@ViewModels';
import type { Edge, Node } from '@xyflow/react';
import { toast } from 'sonner';
import type { BaseNodeData } from '../types';
import type { AutomationStepNodeData } from '../types/stepNodes';
import { isAutomationStepNodeData, isTriggerNode } from '../types/typeGuards';
import { generateNodeId } from '../utils/nodeFactory';
import { NODE_WIDTH, NODE_MIN_HEIGHT } from '../utils/layoutConstants';

/**
 * Clipboard node representation (for storage)
 */
interface ClipboardNode {
	/** Original node ID (for edge remapping) */
	originalId: string;
	/** Node type */
	type: string;
	/** Node data (deep copied) */
	data: BaseNodeData;
	/** Original position */
	position: { x: number; y: number };
	/** Position relative to selection center (for paste offset) */
	relativePosition: { x: number; y: number };
}

/**
 * Clipboard edge representation (for storage)
 */
interface ClipboardEdge {
	/** Original edge ID */
	originalId: string;
	/** Original source node ID */
	sourceOriginalId: string;
	/** Original target node ID */
	targetOriginalId: string;
	/** Source handle (for switch nodes) */
	sourceHandle?: string | null;
	/** Edge type */
	type: string;
	/** Edge data */
	data?: Record<string, unknown>;
}

/**
 * Complete clipboard state
 */
interface ClipboardData {
	nodes: ClipboardNode[];
	edges: ClipboardEdge[];
	copyTimestamp: number;
}

/**
 * Result of a paste operation
 */
export interface PasteResult {
	/** New nodes to add */
	nodes: Node<BaseNodeData>[];
	/** New edges to add */
	edges: Edge[];
	/** IDs of the newly created nodes */
	newNodeIds: string[];
	/** Mapping from original ID to new ID */
	idMapping: Map<string, string>;
}

/**
 * Options for the useCopyPaste hook
 */
export interface UseCopyPasteOptions {
	/** Current nodes in the canvas */
	nodes: Node<BaseNodeData>[];
	/** Current edges in the canvas */
	edges: Edge[];
}

/**
 * Return type for the useCopyPaste hook
 */
export interface UseCopyPasteReturn {
	/** Whether clipboard has content */
	hasClipboard: boolean;
	/** Number of nodes in clipboard */
	clipboardNodeCount: number;

	/** Copy selected nodes to clipboard */
	copySelection: (selectedNodeIds: string[]) => void;
	/** Paste clipboard content at a position */
	pasteAtPosition: (position: { x: number; y: number }) => PasteResult | null;
	/** Clear the clipboard */
	clearClipboard: () => void;
}

/**
 * Calculate bounding box center of a set of nodes
 */
function calculateSelectionCenter(nodes: Node<BaseNodeData>[]): { x: number; y: number } {
	if (nodes.length === 0) {
		return { x: 0, y: 0 };
	}

	let minX = Infinity;
	let maxX = -Infinity;
	let minY = Infinity;
	let maxY = -Infinity;

	for (const node of nodes) {
		const nodeWidth = NODE_WIDTH;
		const nodeHeight = node.measured?.height ?? NODE_MIN_HEIGHT;

		minX = Math.min(minX, node.position.x);
		maxX = Math.max(maxX, node.position.x + nodeWidth);
		minY = Math.min(minY, node.position.y);
		maxY = Math.max(maxY, node.position.y + nodeHeight);
	}

	return {
		x: (minX + maxX) / 2,
		y: (minY + maxY) / 2,
	};
}

/**
 * Hook for managing copy/paste operations in the automation flow canvas.
 *
 * @example
 * ```typescript
 * const { hasClipboard, copySelection, pasteAtPosition } = useCopyPaste({
 *   nodes,
 *   edges,
 * });
 *
 * // Copy selected nodes
 * copySelection(['node-1', 'node-2']);
 *
 * // Paste at viewport center
 * const result = pasteAtPosition({ x: 500, y: 300 });
 * if (result) {
 *   setNodes(prev => [...prev, ...result.nodes]);
 *   setEdges(prev => [...prev, ...result.edges]);
 * }
 * ```
 */
export function useCopyPaste({ nodes, edges }: UseCopyPasteOptions): UseCopyPasteReturn {
	const [clipboardData, setClipboardData] = useState<ClipboardData | null>(null);
	// Ref mirror of clipboard data — allows pasteAtPosition to read the latest
	// clipboard synchronously even when called in the same event loop tick as
	// copySelection (React batches setState, so the state value would be stale).
	const clipboardRef = useRef<ClipboardData | null>(null);

	/**
	 * Copy selected nodes to clipboard
	 */
	const copySelection = useCallback(
		(selectedNodeIds: string[]) => {
			// Copy supports only step nodes (no triggers/segments/phantom nodes).
			// Switch steps are excluded because they require deep cloning of cases + child templates.
			const selectedNodes = nodes.filter(n => {
				if (!selectedNodeIds.includes(n.id)) {
					return false;
				}
				if (isTriggerNode(n)) {
					return false;
				}
				if (!isAutomationStepNodeData(n.data)) {
					return false;
				}
				return (n.data as AutomationStepNodeData).stepType !== Api.AutomationStepType.Switch;
			});

			if (selectedNodes.length === 0) {
				if (selectedNodeIds.length > 0) {
					toast.warning('Only non-conditional step nodes can be copied');
				}
				return;
			}

			const excludedTriggers = nodes.filter(n => selectedNodeIds.includes(n.id) && isTriggerNode(n));
			if (excludedTriggers.length > 0) {
				toast.info('Trigger nodes were excluded from copy');
			}

			const excludedNonSteps = nodes.filter(
				n => selectedNodeIds.includes(n.id) && !isTriggerNode(n) && !isAutomationStepNodeData(n.data)
			);
			if (excludedNonSteps.length > 0) {
				toast.info('Non-step nodes were excluded from copy');
			}

			const excludedSwitches = nodes.filter(
				n =>
					selectedNodeIds.includes(n.id) &&
					isAutomationStepNodeData(n.data) &&
					(n.data as AutomationStepNodeData).stepType === Api.AutomationStepType.Switch
			);
			if (excludedSwitches.length > 0) {
				toast.info('Conditional steps cannot be copied yet');
			}

			// Calculate center of selection for relative positioning
			const center = calculateSelectionCenter(selectedNodes);

			// Create clipboard nodes with relative positions.
			// Deep-clone data so the clipboard is fully independent of the original nodes.
			const clipboardNodes: ClipboardNode[] = selectedNodes.map(node => ({
				originalId: node.id,
				type: node.type ?? 'default',
				data: structuredClone(node.data) as BaseNodeData,
				position: { ...node.position },
				relativePosition: {
					x: node.position.x - center.x,
					y: node.position.y - center.y,
				},
			}));

			// Find edges where BOTH source and target are in selection
			const selectedIdSet = new Set(selectedNodes.map(n => n.id));
			const clipboardEdges: ClipboardEdge[] = edges
				.filter(e => selectedIdSet.has(e.source) && selectedIdSet.has(e.target))
				.map(edge => ({
					originalId: edge.id,
					sourceOriginalId: edge.source,
					targetOriginalId: edge.target,
					sourceHandle: edge.sourceHandle,
					type: edge.type ?? 'default',
					data: edge.data ? { ...edge.data } : undefined,
				}));

			const newClipboard: ClipboardData = {
				nodes: clipboardNodes,
				edges: clipboardEdges,
				copyTimestamp: Date.now(),
			};
			clipboardRef.current = newClipboard;
			setClipboardData(newClipboard);

			toast.success(`Copied ${clipboardNodes.length} node${clipboardNodes.length > 1 ? 's' : ''}`);
		},
		[nodes, edges]
	);

	/**
	 * Paste clipboard content at a position
	 */
	const pasteAtPosition = useCallback(
		(position: { x: number; y: number }): PasteResult | null => {
			// Read from the ref to get the latest clipboard data, even if
			// copySelection was called in the same synchronous tick.
			const currentClipboard = clipboardRef.current;
			if (!currentClipboard || currentClipboard.nodes.length === 0) {
				return null;
			}

			// Create ID mapping: originalId -> newId
			const idMapping = new Map<string, string>();

			// Create new nodes with regenerated IDs
			const newNodes: Node<BaseNodeData>[] = currentClipboard.nodes.map(clipNode => {
				const newId = generateNodeId(clipNode.type);
				idMapping.set(clipNode.originalId, newId);

				// Deep-clone data so each paste produces independent objects.
				const newData = structuredClone(clipNode.data) as BaseNodeData;

				// Update IDs to indicate this is a new unsaved step
				if (isAutomationStepNodeData(newData)) {
					delete (newData as Partial<AutomationStepNodeData>).stepId;
					(newData as AutomationStepNodeData).nodeId = newId;
					// Also clear the automationStep's ID so it's treated as new
					if (newData.automationStep) {
						delete (newData.automationStep as Record<string, unknown>).id;
					}
				}

				return {
					id: newId,
					type: clipNode.type,
					position: {
						x: position.x + clipNode.relativePosition.x,
						y: position.y + clipNode.relativePosition.y,
					},
					data: newData,
				};
			});

			// Recreate edges with new IDs
			const newEdges: Edge[] = [];
			for (const clipEdge of currentClipboard.edges) {
				const newSource = idMapping.get(clipEdge.sourceOriginalId);
				const newTarget = idMapping.get(clipEdge.targetOriginalId);

				if (!newSource || !newTarget) {
					// Should not happen if copy was done correctly
					continue;
				}

				newEdges.push({
					id: `edge-${newSource}-${newTarget}-${Date.now()}`,
					source: newSource,
					target: newTarget,
					sourceHandle: clipEdge.sourceHandle,
					type: clipEdge.type,
					data: clipEdge.data ? { ...clipEdge.data } : undefined,
				});
			}

			return {
				nodes: newNodes,
				edges: newEdges,
				newNodeIds: newNodes.map(n => n.id),
				idMapping,
			};
		},
		// clipboardRef is a ref — stable across renders, no dependency needed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	/**
	 * Clear the clipboard
	 */
	const clearClipboard = useCallback(() => {
		clipboardRef.current = null;
		setClipboardData(null);
	}, []);

	return {
		hasClipboard:
			(clipboardRef.current !== null && clipboardRef.current.nodes.length > 0) ||
			(clipboardData !== null && clipboardData.nodes.length > 0),
		clipboardNodeCount: clipboardRef.current?.nodes.length ?? clipboardData?.nodes.length ?? 0,
		copySelection,
		pasteAtPosition,
		clearClipboard,
	};
}
